"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { Link } from "react-router-dom"
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddOrder = () => {
    const [orderInfo, setOrderInfo] = useState({
        order_date: "",
        order_id: "",
        order_medium: "",
        customer_name: "",
        phone_number: ""
    })

    const [isOrderInfoOpen, setIsOrderInfoOpen] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(false)

    // medium dropdown states
    const [mediums, setMediums] = useState([])
    const [filteredMediums, setFilteredMediums] = useState([])
    const [showMediumDropdown, setShowMediumDropdown] = useState(false)

    useEffect(() => {
        fetchMediums()
    }, [])

    const fetchMediums = async () => {
        try {
            const response = await axios.get(
                `https://logicx.premierlam.com/api/get-pixel-medium`,
                {
                    headers: {
                        authorization: localStorage.getItem("accessToken"),
                        "Content-Type": "application/json",
                    },
                }
            )

            if (response.data?.data) {
                setMediums(response.data.data)
                setFilteredMediums(response.data.data)
            }
        } catch (err) {
            console.error("Error fetching mediums:", err)
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target

        // phone validation only digits, max 10
        if (name === "phone_number") {
            const numericValue = value.replace(/\D/g, "")   // remove non-digits
            if (numericValue.length <= 10) {
                setOrderInfo((prev) => ({ ...prev, phone_number: numericValue }))
            }
            return
        }

        setOrderInfo((prev) => ({ ...prev, [name]: value }))

        if (name === "order_medium") {
            const filtered = mediums.filter((m) =>
                m.medium_name.toLowerCase().includes(value.toLowerCase())
            )
            setFilteredMediums(filtered)
            setShowMediumDropdown(true)
        }
    }

    const handleMediumSelect = (name) => {
        setOrderInfo((prev) => ({ ...prev, order_medium: name }))
        setShowMediumDropdown(false)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        try {
            setIsSubmitting(true)
            setError(null)
            setSuccess(false)

            const response = await axios.post(
                `https://logicx.premierlam.com/api/pixel-create-orders`,
                {
                    order_date: formatDateToDDMMMYYYY(orderInfo.order_date),
                    order_id: orderInfo.order_id,
                    order_medium: orderInfo.order_medium,
                    customer_name: orderInfo.customer_name,
                    phone_number: orderInfo.phone_number,
                },
                {
                    headers: {
                        authorization: localStorage.getItem("accessToken"),
                        "Content-Type": "application/json",
                    },
                }
            )

            if (response.data) {
                  toast.success("Order created successfully!");
                setSuccess(true)
                setOrderInfo({
                    order_date: "",
                    order_id: "",
                    order_medium: "",
                    customer_name: "",
                    phone_number: ""
                })
                setTimeout(() => {
                    setSuccess(false)
                }, 3000)
            }
        } catch (err) {
              toast.error(err.response?.data?.message || "Failed to create order");
        } finally {
            setIsSubmitting(false)
        }
    }
const formatDateToDDMMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

    return (
        <><ToastContainer position="top-right" autoClose={3000} />
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">

                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-700">Create Order</h1>
                        <p className="text-gray-500">Create new order</p>
                    </div>
                    <Link
                        to="/all-orders"
                        className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Orders
                    </Link>
                </div>

                {error && (
                    <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}
                {success && (
                    <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-600">Order created successfully!</p>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div
                            className="flex justify-between items-center p-6 cursor-pointer"
                            onClick={() => setIsOrderInfoOpen(!isOrderInfoOpen)}
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-500">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h2 className="text-lg font-medium text-gray-700">Order Information</h2>
                            </div>

                            <svg
                                className={`w-5 h-5 text-gray-400 transition-transform ${isOrderInfoOpen ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>

                        {isOrderInfoOpen && (
                            <div className="p-6 border-t border-gray-100">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                    {/* Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Order Date *</label>
                                        <input
                                            type="date"
                                            name="order_date"
                                            value={orderInfo.order_date}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
                                            required
                                        />
                                    </div>

                                    {/* Order ID */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Order ID *</label>
                                        <input
                                            type="text"
                                            name="order_id"
                                            value={orderInfo.order_id}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
                                            required
                                        />
                                    </div>

                                    {/* Medium */}
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Order Medium *</label>
                                        <input
                                            type="text"
                                            name="order_medium"
                                            value={orderInfo.order_medium}
                                            onChange={handleInputChange}
                                            onFocus={() => setShowMediumDropdown(true)}
                                            onBlur={() => setTimeout(() => setShowMediumDropdown(false), 200)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
                                            placeholder="Search or select medium"
                                            required
                                        />
                                        {showMediumDropdown && (
                                            <ul className="absolute z-10 bg-white border border-gray-200 rounded-md shadow-md mt-1 w-full max-h-40 overflow-y-auto">
                                                {filteredMediums.length > 0 ? (
                                                    filteredMediums.map((medium) => (
                                                        <li
                                                            key={medium.id}
                                                            onClick={() => handleMediumSelect(medium.medium_name)}
                                                            className="px-4 py-2 hover:bg-orange-100 cursor-pointer text-sm"
                                                        >
                                                            {medium.medium_name}
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li className="px-4 py-2 text-gray-500 text-sm">No results found</li>
                                                )}
                                            </ul>
                                        )}
                                    </div>

                                    {/* Customer */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                                        <input
                                            type="text"
                                            name="customer_name"
                                            value={orderInfo.customer_name}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
                                            required
                                        />
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                        <input
                                            type="text"
                                            name="phone_number"
                                            value={orderInfo.phone_number}
                                            maxLength={10}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 border rounded-md focus:ring-2 outline-none ${orderInfo.phone_number.length > 0 && orderInfo.phone_number.length !== 10
                                                ? "border-red-500 focus:ring-red-500"
                                                : "border-gray-300 focus:ring-orange-500"
                                                }`}
                                            required
                                        />
                                        {orderInfo.phone_number.length > 0 && orderInfo.phone_number.length !== 10 && (
                                            <p className="text-xs text-red-500 mt-1">Phone number must be 10 digits</p>
                                        )}
                                    </div>


                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" className="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Creating..." : "Add Order"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </>
    )
}

export default AddOrder
